import { useState } from 'react'
import { SettingsOutlined } from '@mui/icons-material'
import { CircularProgress, IconButton, ListItemText, Menu, MenuItem, MenuList } from '@mui/material'
import { useSnackbar } from 'notistack'

import DeviceLocationModal from '../../../../components/device-location-modal'
import Api from '../../../../utils/api'

import useStyles from './index.styles'

// Node types that support locate and mute
const NODE_TYPES_WITH_EXTRA_ACTIONS = ['leak3', 'temp3']

interface IProps {
  device: IDeviceType | null
  refetchDevice?: () => void
}

const DeviceDetailsMenu = (props: IProps) => {
  const { device, refetchDevice } = props

  const classes = useStyles
  const { enqueueSnackbar } = useSnackbar()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [locateSubmitting, setLocateSubmitting] = useState<boolean>(false)
  const [muteSubmitting, setMuteSubmitting] = useState<boolean>(false)

  const [showDeviceLocationModal, setShowDeviceLocationModal] = useState<boolean>(false)

  const supportsLocateAndSound = device?.node?.nodeTypeName ? NODE_TYPES_WITH_EXTRA_ACTIONS.includes(device.node.nodeTypeName) : false
  const isMuted = device?.node?.mute === true

  const chirpValue = device?.currentValues?.chirp?.value as string | undefined

  // Determine if device is currently in locate mode
  const isLocating = chirpValue === 'started'

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleShowDeviceLocationModal = () => {
    setShowDeviceLocationModal(true)
    handleClose()
  }

  const handleLocate = async () => {
    if (!device) return

    const action = isLocating ? 'cancel' : 'start'

    setLocateSubmitting(true)

    try {
      const res = await Api.post(`/api/v2/protect/devices/${device.node.id}/locate`, { action })

      if (res.ok) {
        enqueueSnackbar(action === 'start' ? 'Locate request sent. Device alarm will sound at next check-in.' : 'Locate cancelled successfully.', {
          variant: 'success',
        })
      } else {
        enqueueSnackbar(`Failed to ${action === 'start' ? 'start locate' : 'cancel locate'}`, { variant: 'error' })
      }
    } catch {
      enqueueSnackbar(`There was a problem trying to ${action === 'start' ? 'locate the device' : 'cancel the device alarm'}.`, { variant: 'error' })
    } finally {
      setLocateSubmitting(false)
      handleClose()
    }
  }

  const handleToggleMute = async () => {
    if (!device) return

    if (device.isOffline) {
      enqueueSnackbar('Sensor must be online to use this feature', { variant: 'error' })
      handleClose()
      return
    }

    const toggle = !isMuted // true to mute, false to unmute

    setMuteSubmitting(true)

    try {
      const res = await Api.post(`/api/v2/protect/devices/${device.node.id}/mute`, { toggle })

      if (res.ok) {
        enqueueSnackbar(toggle ? 'Device is muted' : 'Device is unmuted', {
          variant: 'success',
        })
        if (refetchDevice) {
          refetchDevice()
        }
      } else {
        enqueueSnackbar(`Failed to ${toggle ? 'mute' : 'unmute'} device`, { variant: 'error' })
      }
    } catch {
      enqueueSnackbar(`There was a problem trying to ${toggle ? 'mute' : 'unmute'} the device.`, { variant: 'error' })
    } finally {
      setMuteSubmitting(false)
      handleClose()
    }
  }

  return (
    <>
      <IconButton onClick={handleClick} aria-label="Device settings" disabled={!device}>
        <SettingsOutlined />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'device-settings-menu',
          },
        }}
      >
        <MenuList sx={classes.menuList}>
          <MenuItem onClick={handleShowDeviceLocationModal}>
            <ListItemText>Add/View Photo</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleShowDeviceLocationModal}>
            <ListItemText>Edit Placement Notes</ListItemText>
          </MenuItem>

          {supportsLocateAndSound && (
            <MenuItem onClick={handleLocate} disabled={locateSubmitting}>
              <ListItemText>
                {locateSubmitting ? (
                  <>
                    <CircularProgress size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    {isLocating ? 'Cancelling...' : 'Starting...'}
                  </>
                ) : isLocating ? (
                  'Stop Locate'
                ) : (
                  'Locate'
                )}
              </ListItemText>
            </MenuItem>
          )}

          {/* TODO: This will be a future feature */}
          {/* <MenuItem onClick={handleTogglePause}>
            <ListItemText>Pause</ListItemText>
          </MenuItem> */}

          {supportsLocateAndSound && (
            <MenuItem onClick={handleToggleMute} disabled={muteSubmitting}>
              <ListItemText>
                {muteSubmitting ? (
                  <>
                    <CircularProgress size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    {isMuted ? 'Unmuting...' : 'Muting...'}
                  </>
                ) : isMuted ? (
                  'Unmute'
                ) : (
                  'Mute'
                )}
              </ListItemText>
            </MenuItem>
          )}
        </MenuList>
      </Menu>

      <DeviceLocationModal
        key={device?.node?.id}
        open={showDeviceLocationModal}
        onClose={() => setShowDeviceLocationModal(false)}
        device={device}
        onSuccess={refetchDevice}
      />
    </>
  )
}

export default DeviceDetailsMenu
